import React, { useEffect } from 'react'
import { Input, Form, message, Typography } from 'antd'
import { LockOutlined, UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import AuthForm from '../components/common/AuthForm/AuthForm'
import LanguageSwitcher from '../components/common/LanguageSwitcher'
import loginBrandLogo from '../assets/login-brand-logo.jpg'
import { useAuth } from '../hooks/useAuth'
import { useAuthForm } from '../hooks/useAuthForm'
import { LoginFormValues } from '../types/form'
import {
    AUTH_REDIRECT_REASON_ACCOUNT_CHANGED,
    AUTH_REDIRECT_REASON_PERMISSION_CHANGED,
    AUTH_REDIRECT_REASON_PASSWORD_CHANGED,
    AUTH_REDIRECT_REASON_STORAGE_KEY,
    POST_LOGIN_REDIRECT_STORAGE_KEY,
} from '../utils/axiosConfig'

const { Text } = Typography

const Login = (): React.ReactElement => {
    const { t } = useTranslation(['auth', 'errors'])
    const { login, refreshUser } = useAuth()
    const { loading, error, handleSubmit, state } = useAuthForm('/')

    const metricCards = [
        {
            key: 'uptime',
            value: t('login.metrics.uptime.value'),
            label: t('login.metrics.uptime.label'),
        },
        {
            key: 'ai',
            value: t('login.metrics.ai.value'),
            label: t('login.metrics.ai.label'),
        },
        {
            key: 'iot',
            value: t('login.metrics.iot.value'),
            label: t('login.metrics.iot.label'),
        },
    ]

    useEffect(() => {
        const redirectReason = sessionStorage.getItem(
            AUTH_REDIRECT_REASON_STORAGE_KEY
        )

        const redirectReasonMessages: Record<string, string> = {
            [AUTH_REDIRECT_REASON_PERMISSION_CHANGED]: t(
                'errors:auth.permissionChanged',
                {
                    defaultValue: 'Permissions changed. Please log in again.',
                }
            ),
            [AUTH_REDIRECT_REASON_PASSWORD_CHANGED]: t(
                'errors:auth.passwordChanged',
                {
                    defaultValue: 'Password changed. Please log in again.',
                }
            ),
            [AUTH_REDIRECT_REASON_ACCOUNT_CHANGED]: t(
                'errors:auth.accountChanged',
                {
                    defaultValue:
                        'Account information changed. Please log in again.',
                }
            ),
        }

        const redirectReasonMessage = redirectReason
            ? redirectReasonMessages[redirectReason]
            : undefined
        if (redirectReasonMessage) {
            message.error(redirectReasonMessage)
        }

        if (redirectReason) {
            sessionStorage.removeItem(AUTH_REDIRECT_REASON_STORAGE_KEY)
        }
    }, [t])

    const onFinish = async (values: LoginFormValues): Promise<void> => {
        await handleSubmit(
            async () => {
                const success = await login(values.account, values.password)
                if (!success) {
                    throw new Error(t('login.loginFailed'))
                }

                const currentUser = await refreshUser()
                if (!currentUser) {
                    throw new Error(t('login.loginFailed'))
                }

                sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY)

                return { to: '/' }
            },
            values,
            t('login.loginSuccess'),
            (redirectTarget) => redirectTarget
        )
    }

    return (
        <main className="login-reference-shell">
            <section className="login-reference-card">
                <aside
                    className="login-reference-visual"
                    aria-label={t('login.visualAria')}
                >
                    <div className="login-reference-brand-lockup">
                        <span
                            className="login-reference-brand-mark"
                            aria-hidden="true"
                        >
                            <img
                                src={loginBrandLogo}
                                alt=""
                                className="login-reference-brand-mark-image"
                            />
                        </span>
                        <Text className="login-reference-brand-name">
                            {t('login.brandName')}
                        </Text>
                    </div>

                    <div className="login-reference-wave" aria-hidden="true" />

                    <div className="login-reference-ai-badge" aria-hidden="true">
                        <Text className="login-reference-ai-value">
                            {t('login.metrics.ai.value')}
                        </Text>
                        <Text className="login-reference-ai-label">
                            {t('login.metrics.ai.label')}
                        </Text>
                    </div>

                    <div className="login-reference-metrics" aria-hidden="true">
                        {metricCards.map((metric) => (
                            <article
                                className="login-reference-metric"
                                key={metric.key}
                            >
                                <Text className="login-reference-metric-value">
                                    {metric.value}
                                </Text>
                                <Text className="login-reference-metric-label">
                                    {metric.label}
                                </Text>
                            </article>
                        ))}
                    </div>
                </aside>

                <section
                    className="login-reference-form-panel"
                    aria-label={t('login.formAria')}
                >
                    <div className="login-reference-form-topbar">
                        <LanguageSwitcher
                            placement="login"
                            size="small"
                            className="login-language-switcher"
                            style={{ width: 78 }}
                        />
                    </div>
                    <AuthForm<LoginFormValues>
                        title={t('login.title')}
                        subtitle={t('login.subtitle')}
                        formName="login"
                        onFinish={onFinish}
                        loading={loading}
                        error={error}
                        successMessage={state?.message}
                        submitText={t('login.loginButton')}
                        containerClassName="login-reference-auth-container"
                        cardClassName="login-reference-auth-card"
                    >
                        <Form.Item
                            name="account"
                            label={t('login.accountLabel')}
                            rules={[
                                {
                                    required: true,
                                    message: t('validation.accountRequired'),
                                },
                            ]}
                        >
                            <Input
                                className="login-autofill-field"
                                prefix={<UserOutlined />}
                                placeholder={t('login.accountPlaceholder')}
                                size="large"
                                autoComplete="username"
                                aria-label={t('login.accountLabel')}
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label={t('login.passwordLabel')}
                            rules={[
                                {
                                    required: true,
                                    message: t('validation.passwordRequired'),
                                },
                            ]}
                        >
                            <Input.Password
                                className="login-autofill-field"
                                prefix={<LockOutlined />}
                                placeholder={t('login.passwordPlaceholder')}
                                size="large"
                                autoComplete="current-password"
                                aria-label={t('login.passwordLabel')}
                            />
                        </Form.Item>
                    </AuthForm>
                </section>
            </section>
        </main>
    )
}

export default Login
